import s from './KeywordsTable.module.scss';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { DropdownItem, Flex } from '@tremor/react';
import { FC, useCallback, useMemo, useState } from 'react';
import { CoreTable } from '@components/Tables/CoreTable/CoreTable';
import { TagFromStore } from '@/types/types';
import { useUserPermissionsStore } from '@/stores/userPermissionsStore';
import { Checkbox } from '@/components/layouts/Inputs/Checkbox/Checkbox';
import { KeywordCellElement } from './KeywordCellElement';
import { TagCellElement } from './TagCellElement';
import { OutlinedButton } from '@/components/layouts/Buttons/OutlinedButton/OutlinedButton';
import { OutlinedSelectInput } from '@/components/layouts/Inputs/OutlinedSelectInput/OutlinedSelectInput';
import { TableActions } from './TableActions';
import { Pagination } from '../Pagination/Pagination';
import { AssignTagsModal } from '@/components/layouts/Modals/AssignTagsModal';
import { ITEMS_PER_PAGE } from '@/utils/constants';
import { AddTagIcon, DeleteIcon } from '@/assets';
import { useKeywordsStore } from '@/stores/keywordsStore';
import { KeywordsService } from '@/services/dataforseo/keywordsService';
import { useWorkspaceIdFromParams } from '@/hooks/useWorkspaceIdFromParams';
import all_countries_raw from '@/assets/csv/language_codes.csv';

const all_countries = all_countries_raw as { alpha2: string }[];
all_countries.sort((a, b) => a['alpha2'].localeCompare(b['alpha2']));

interface Props {
  keywords: TableKeyword[];
  changeLanguageHandler: (language: string, id: number) => void;
  handleDeleteKeyword: (rowId: number) => void;
  handleDeleteTag: (rowId: number, tagId: string) => void;
  handleDeleteSelectedKeywords: (selectedKeywords: Record<number, boolean>) => void;
  tags: TagFromStore[];
  totalItems: number;
  itemsPerPage?: number;
  searchValue: string;
}

export type TableKeyword = {
  id: string;
  keyword: string;
  assignedTags: { id: string; label: string }[];
  language: string;
  deletionApproval?: boolean;
};

const columnHelper = createColumnHelper<TableKeyword>();

export const KeywordsTable: FC<Props> = ({
  keywords,
  changeLanguageHandler,
  handleDeleteKeyword,
  handleDeleteTag,
  handleDeleteSelectedKeywords,
  tags,
  searchValue,
}) => {
  const [rowSelection, setRowSelection] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedKeyword, setEditedKeyword] = useState<TableKeyword>({
    keyword: '',
    language: 'en',
    id: '',
    assignedTags: [],
  });
  const [prevSearchValue, setPrevSearchValue] = useState(searchValue);

  const setKeywords = useKeywordsStore((state) => state.setKeywords);
  const workspaceId = useWorkspaceIdFromParams() as string;
  const [pagination, setPagination] = useState({
    pageIndex: 0, //initial page index
    pageSize: ITEMS_PER_PAGE, //default page size
  });

  const workspaceUserRole = useUserPermissionsStore(
    (state) => state.workspacePermission?.role,
  );

  const setEditedKeywordById = useCallback(
    (keywordId: string) => {
      const keyword = keywords.find((k) => k.id === keywordId);
      setEditedKeyword(keyword as TableKeyword);
    },
    [keywords],
  );
  const pageCount = Math.ceil(keywords.length / ITEMS_PER_PAGE);

  const columns = useMemo(
    () => [
      columnHelper.accessor('deletionApproval', { cell: '', header: '' }),
      columnHelper.accessor('id', { cell: '', header: '' }),
      columnHelper.display({
        id: 'checkbox',
        cell: ({ row }) => (
          <Checkbox
            disabled={
              !!row.getValue('deletionApproval') && workspaceUserRole === 'editor'
            }
            row={row}
          />
        ),
      }),
      columnHelper.accessor('keyword', {
        cell: (props) => <KeywordCellElement props={props} />,
      }),
      columnHelper.accessor('assignedTags', {
        header: 'assigned tags',
        cell: (props) => (
          <section className={s.tagsCell}>
            {props.getValue().map((tag) => (
              <TagCellElement
                onClick={() => handleDeleteTag(parseInt(props.row.id), tag.id)}
                key={tag.id}
                tag={tag.label}
              />
            ))}
          </section>
        ),
      }),
      columnHelper.display({
        minSize: 250,
        id: 'addButton',
        cell: (props) => {
          return (
            <Flex style={{ width: '150px', justifyContent: 'center' }}>
              <OutlinedButton
                className="addTagButton"
                style={{ marginRight: 'auto', marginLeft: '.5rem' }}
                icon={AddTagIcon}
                iconAltText="add tag icon"
                onClick={() => {
                  setEditedKeywordById(props.row.getValue('id'));
                  setIsModalOpen(true);
                }}
              >
                Add
              </OutlinedButton>
            </Flex>
          );
        },
      }),
      columnHelper.accessor('language', {
        minSize: 300,
        cell: (props) => (
          <div style={{ width: '250px' }}>
            <span className={s.languageCellText}>{props.getValue()}</span>
            <OutlinedSelectInput
              style={{ width: 'fit-content', marginLeft: '1rem' }}
              onChange={(value) => {
                changeLanguageHandler(value, parseInt(props.row.id));
              }}
              className="keywords_table_language_select"
              defaultValue={props.getValue()}
            >
              {all_countries.map(({ alpha2 }) => (
                <DropdownItem key={alpha2} value={alpha2} text={alpha2.toUpperCase()} />
              ))}
            </OutlinedSelectInput>
          </div>
        ),
      }),
      columnHelper.display({
        header: 'Options',
        cell: (props) => {
          {
            return !!props.row.getValue('deletionApproval') &&
              workspaceUserRole === 'editor' ? null : (
              <OutlinedButton
                onClick={() => handleDeleteKeyword(parseInt(props.row.id))}
                icon={DeleteIcon}
                iconAltText="delete keywords icon"
              >
                Remove
              </OutlinedButton>
            );
          }
        },
      }),
    ],
    [
      changeLanguageHandler,
      handleDeleteKeyword,
      handleDeleteTag,
      setEditedKeywordById,
      workspaceUserRole,
    ],
  );

  const table = useReactTable({
    data: keywords,
    columns,
    state: {
      rowSelection,
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
  });

  if (prevSearchValue !== searchValue) {
    setRowSelection({});
    setPrevSearchValue(searchValue);
    setPagination({
      pageIndex: 0,
      pageSize: ITEMS_PER_PAGE,
    });
  }

  const handlePageClick = (event: {
    index: number | null;
    selected: number;
    nextSelectedPage: number | undefined;
    event: object;
    isPrevious: boolean;
    isNext: boolean;
    isBreak: boolean;
    isActive: boolean;
  }) => {
    if (!keywords[keywords.length - 1]) {
      KeywordsService.getAllKeywords(workspaceId, 'dataforseo').then((res) => {
        setKeywords(res);
        setNewPageIndex();
      });
      return;
    }

    function setNewPageIndex() {
      if (event.isNext) {
        table.getCanNextPage() && table.nextPage();
      } else if (event.isPrevious) {
        table.getCanPreviousPage() && table.previousPage();
      } else if (typeof event.nextSelectedPage === 'number') {
        table.setPageIndex(event.nextSelectedPage);
      }
    }

    setNewPageIndex();
  };

  return (
    <section className={s.wrapper}>
      <TableActions
        table={table}
        onSelectAll={async () => {
          // fetches all keywords if only first page is loaded
          if (!keywords[keywords.length - 1]) {
            await KeywordsService.getAllKeywords(workspaceId, 'dataforseo').then(
              (res) => {
                setKeywords(res);
              },
            );
            return;
          }
        }}
        onDeleteSelected={async () => {
          handleDeleteSelectedKeywords(rowSelection);
          table.setRowSelection({});
        }}
      />
      <section className={s.tableContainer}>
        <CoreTable className={s.cellsHover} table={table} />
      </section>
      <Pagination
        key={searchValue}
        onClick={handlePageClick}
        pageCount={pageCount}
        currentPage={pagination.pageIndex}
        totalPages={pageCount}
      />
      <TableActions
        table={table}
        onDeleteSelected={() => {
          handleDeleteSelectedKeywords(rowSelection);
          table.setRowSelection({});
        }}
      />
      <AssignTagsModal
        editedKeyword={editedKeyword}
        tags={tags}
        isOpen={isModalOpen}
        setClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};
